import {
  AggregateEnergyFlow,
  FinancialPerformanceScenarios,
  getFinancialPerformance,
  getFinancialPerformanceProcurement,
  Site,
} from "@inrange/building-manager-api-client/models-site";
import { SummaryBoxMetric } from "@inrange/theme-components";
import {
  formatCurrency,
  formatUnits,
} from "@inrange/theme-components/formatting";

export const generateProcuredMetrics = (
  site: Site,
  lastMonthEnergyActuals: AggregateEnergyFlow | undefined,
  lastMonthFinancialActuals: FinancialPerformanceScenarios | undefined,
  isOwnerOccupier: boolean,
  investmentModel: string,
  isOnsitePvLease: boolean
) => {
  if (isOnsitePvLease) {
    if (site.totalBatterySummary) {
      return [
        {
          value: `${formatUnits(lastMonthEnergyActuals?.behindMeter ?? 0, "kWh", 2)}`,
          valueSubText: "On-site direct from PV and battery",
          dataTestId: "procured-behind-meter-monthly",
        },
      ];
    }
    return [
      {
        value: `${formatUnits(lastMonthEnergyActuals?.behindMeter ?? 0, "kWh", 2)}`,
        valueSubText: "On-site direct from PV",
        dataTestId: "procured-behind-meter-monthly",
      },
    ];
  }
  const result: {
    value: string;
    valueSubText: string;
    dataTestId: string;
  }[] = [];
  if (site.totalInstalledCapacity > 0) {
    result.push({
      value:
        `${formatCurrency(getFinancialPerformanceProcurement(lastMonthFinancialActuals, isOwnerOccupier, investmentModel)?.costOnsitePV ?? 0, site.currencyCode, 2)} ` +
        `(${formatUnits(lastMonthEnergyActuals?.behindMeter ?? 0, "kWh", 2)})`,
      valueSubText: "On-site direct from PV",
      dataTestId: "procured-behind-meter-monthly",
    });
  }
  if (site.sdmMatches.filter((match) => match.buyerId === site.id).length > 0) {
    result.push({
      value:
        `${formatCurrency(getFinancialPerformanceProcurement(lastMonthFinancialActuals, isOwnerOccupier, investmentModel)?.costNetworkImport ?? 0, site.currencyCode, 2)} ` +
        `(${formatUnits(lastMonthEnergyActuals?.networkImport ?? 0, "kWh", 2)})`,
      valueSubText: "Imported from network\nexcl. non-commodity costs",
      dataTestId: "procured-network-import-monthly",
    });
  }
  return result;
};

export const generateRevenueMetrics = (
  site: Site,
  lastMonthEnergyActuals: AggregateEnergyFlow | undefined,
  lastMonthFinancialActuals: FinancialPerformanceScenarios | undefined,
  isOwnerOccupier: boolean,
  isOnsitePvLease: boolean,
  investmentModel: string
) => {
  const metrics: SummaryBoxMetric[] = [];
  if (!isOnsitePvLease) {
    metrics.push({
      value:
        `${formatCurrency(getFinancialPerformance(lastMonthFinancialActuals, isOwnerOccupier, investmentModel)?.revenueOnsitePV ?? 0, site.currencyCode, 2)} ` +
        `(${formatUnits(lastMonthEnergyActuals?.behindMeter ?? 0, "kWh", 2)})`,
      valueSubText: "On-site direct from PV",
      dataTestId: "revenue-behind-meter-monthly",
    });
  }
  if (!site.hasAllZeroExportLimit) {
    metrics.push({
      value:
        `${formatCurrency(getFinancialPerformance(lastMonthFinancialActuals, isOwnerOccupier, investmentModel)?.revenueExport ?? 0, site.currencyCode, 2)} ` +
        `(${formatUnits((lastMonthEnergyActuals?.networkExport ?? 0) + (lastMonthEnergyActuals?.exported ?? 0), "kWh", 2)})`,
      valueSubText: "Exported to network",
      dataTestId: "revenue-export-monthly",
    });
  }
  return metrics;
};

export const getTariffHeader = (
  isOnsitePvLease: boolean,
  tariffMetricsLength: number
) => {
  return isOnsitePvLease
    ? tariffMetricsLength === 1
      ? "PV lease"
      : "PV lease & tariffs"
    : "Tariffs";
};
